import React, { useState, useEffect, useCallback, useRef } from 'react';
import './BoulderGame.css';
import Stickman from './Stickman';
import Boulder from './Boulder';
import Mountain from './Mountain';
import Store from './Store';
import Achievements from './Achievements';
import Gambling from './Gambling';
import Microtransactions from './Microtransactions';
import Automation from './Automation';
import RockMarket, { RockType, RockPrices, RockHoldings, RockPriceHistory, ROCK_TYPES } from './RockMarket';
import DropBallAnimation from './DropBallAnimation';
import NgPlusUpgrade from './NgPlusUpgrade';
import Customization, { BoulderCustomization, CUSTOMIZATIONS } from './Customization';
import Farm from './Farm';

interface GameState {
  position: number;
  score: number;
  isMoving: boolean;
  lastKey: string | null;
}

const BoulderGame: React.FC = () => {
  const calculateNewPrice = (currentPrice: number, type: RockType): number => {
    const config = ROCK_TYPES[type];
    const volatility = config.volatility;
    const changePercent = (Math.random() * 2 - 1) * volatility;
    let newPrice = currentPrice * (1 + changePercent);
    newPrice = Math.max(config.basePrice, Math.min(config.maxPrice, newPrice));
    const roundedPrice = Math.round(newPrice);
    
    if (roundedPrice === currentPrice) {
      if (changePercent > 0 && currentPrice < config.maxPrice) {
        return currentPrice + 1;
      } else if (changePercent < 0 && currentPrice > config.basePrice) {
        return currentPrice - 1;
      }
    }
    
    return roundedPrice;
  };

  // Load saved data from localStorage
  const loadSavedData = () => {
    try {
      const savedScore = localStorage.getItem('boulderGame_score');
      const savedCoins = localStorage.getItem('boulderGame_coins');
      const savedMaxCoins = localStorage.getItem('boulderGame_maxCoins');
      const savedUpgrades = localStorage.getItem('boulderGame_upgrades');
      const savedAutomation = localStorage.getItem('boulderGame_automation');
      const savedStoreUnlocked = localStorage.getItem('boulderGame_storeUnlocked');
      const savedAchievementsUnlocked = localStorage.getItem('boulderGame_achievementsUnlocked');
      const savedAutomationUnlocked = localStorage.getItem('boulderGame_automationUnlocked');
      const savedGamblingUnlocked = localStorage.getItem('boulderGame_gamblingUnlocked');
      const savedHasUsedGambling = localStorage.getItem('boulderGame_hasUsedGambling');
      const savedGamblingWins = localStorage.getItem('boulderGame_gamblingWins');
      const savedGamblingLosses = localStorage.getItem('boulderGame_gamblingLosses');
      const savedGamblingCoinsWon = localStorage.getItem('boulderGame_gamblingCoinsWon');
      const savedGamblingCoinsSpent = localStorage.getItem('boulderGame_gamblingCoinsSpent');
      const savedGamblingAutoplayUnlocked = localStorage.getItem('boulderGame_gamblingAutoplayUnlocked');
      const savedGamblingSlotCount = localStorage.getItem('boulderGame_gamblingSlotCount');
      const savedMicroUnlocked = localStorage.getItem('boulderGame_microUnlocked');
      const savedShinyRocks = localStorage.getItem('boulderGame_shinyRocks');
      const savedShinyUpgrades = localStorage.getItem('boulderGame_shinyUpgrades');
      const savedMahjongPurchased = localStorage.getItem('boulderGame_mahjongPurchased');
      const savedRockMarketUnlocked = localStorage.getItem('boulderGame_rockMarketUnlocked');
      const savedRockHoldings = localStorage.getItem('boulderGame_rockHoldings');
      const savedRockPrices = localStorage.getItem('boulderGame_rockPrices');
      const savedRockPriceHistory = localStorage.getItem('boulderGame_rockPriceHistory');
      const savedScoreMultiplier = localStorage.getItem('boulderGame_scoreMultiplier');
      const savedCoinMultiplier = localStorage.getItem('boulderGame_coinMultiplier');
      const savedCustomizationUnlocked = localStorage.getItem('boulderGame_customizationUnlocked');
      const savedOwnedCustomizations = localStorage.getItem('boulderGame_ownedCustomizations');
      const savedSelectedCustomization = localStorage.getItem('boulderGame_selectedCustomization');
      const savedFarmUnlocked = localStorage.getItem('boulderGame_farmUnlocked');

      return {
        score: savedScore ? parseInt(savedScore, 10) : 0,
        coins: savedCoins ? parseInt(savedCoins, 10) : 0,
        maxCoins: savedMaxCoins ? parseInt(savedMaxCoins, 10) : (savedCoins ? parseInt(savedCoins, 10) : 0),
        upgrades: savedUpgrades ? JSON.parse(savedUpgrades) : { happiness: 0, productivity: 0, boulderSize: 0 },
        automation: savedAutomation ? JSON.parse(savedAutomation) : { manager: 0, devConsole: 0, cicd: 0 },
        storeUnlocked: savedStoreUnlocked === 'true',
        achievementsUnlocked: savedAchievementsUnlocked === 'true',
        automationUnlocked: savedAutomationUnlocked === 'true',
        gamblingUnlocked: savedGamblingUnlocked === 'true',
        mahjongPurchased: savedMahjongPurchased === 'true',
        hasUsedGambling: savedHasUsedGambling === 'true',
        gamblingWins: savedGamblingWins ? parseInt(savedGamblingWins, 10) : 0,
        gamblingLosses: savedGamblingLosses ? parseInt(savedGamblingLosses, 10) : 0,
        gamblingCoinsWon: savedGamblingCoinsWon ? parseInt(savedGamblingCoinsWon, 10) : 0,
        gamblingCoinsSpent: savedGamblingCoinsSpent ? parseInt(savedGamblingCoinsSpent, 10) : 0,
        gamblingAutoplayUnlocked: savedGamblingAutoplayUnlocked === 'true',
        gamblingSlotCount: savedGamblingSlotCount ? parseInt(savedGamblingSlotCount, 10) : 3,
        microUnlocked: savedMicroUnlocked === 'true',
        shinyRocks: savedShinyRocks ? parseInt(savedShinyRocks, 10) : 0,
        shinyUpgrades: savedShinyUpgrades ? JSON.parse(savedShinyUpgrades) : { parentValidation: 0, overpricedCoffee: 0 },
        rockMarketUnlocked: savedRockMarketUnlocked === 'true',
        rockHoldings: savedRockHoldings ? JSON.parse(savedRockHoldings) : { rock: 0, stone: 0, gem: 0, meteorite: 0, asteroid: 0 },
        rockPrices: savedRockPrices ? JSON.parse(savedRockPrices) : {
          rock: ROCK_TYPES.rock.basePrice,
          stone: ROCK_TYPES.stone.basePrice,
          gem: ROCK_TYPES.gem.basePrice,
          meteorite: ROCK_TYPES.meteorite.basePrice,
          asteroid: ROCK_TYPES.asteroid.basePrice,
        } as RockPrices,
        rockPriceHistory: savedRockPriceHistory ? JSON.parse(savedRockPriceHistory) : { rock: [], stone: [], gem: [], meteorite: [], asteroid: [] },
        scoreMultiplier: savedScoreMultiplier ? parseInt(savedScoreMultiplier, 10) : 1,
        coinMultiplier: savedCoinMultiplier ? parseInt(savedCoinMultiplier, 10) : 1,
        customizationUnlocked: savedCustomizationUnlocked === 'true',
        ownedCustomizations: savedOwnedCustomizations ? JSON.parse(savedOwnedCustomizations) : ['default'],
        selectedCustomization: savedSelectedCustomization ? savedSelectedCustomization as BoulderCustomization : 'default',
        farmUnlocked: savedFarmUnlocked === 'true',
      };
    } catch (error) {
      console.error('Error loading saved data:', error);
      return {
        score: 0,
        coins: 0,
        maxCoins: 0,
        upgrades: { happiness: 0, productivity: 0, boulderSize: 0 },
        automation: { manager: 0, devConsole: 0, cicd: 0 },
        storeUnlocked: false,
        achievementsUnlocked: false,
        automationUnlocked: false,
        gamblingUnlocked: false,
        mahjongPurchased: false,
        hasUsedGambling: false,
        gamblingWins: 0,
        gamblingLosses: 0,
        gamblingCoinsWon: 0,
        gamblingCoinsSpent: 0,
        gamblingAutoplayUnlocked: false,
        gamblingSlotCount: 3,
        microUnlocked: false,
        shinyRocks: 0,
        shinyUpgrades: { parentValidation: 0, overpricedCoffee: 0 },
        rockMarketUnlocked: false,
        rockHoldings: { rock: 0, stone: 0, gem: 0, meteorite: 0, asteroid: 0 },
        rockPrices: {
          rock: ROCK_TYPES.rock.basePrice,
          stone: ROCK_TYPES.stone.basePrice,
          gem: ROCK_TYPES.gem.basePrice,
          meteorite: ROCK_TYPES.meteorite.basePrice,
          asteroid: ROCK_TYPES.asteroid.basePrice,
        } as RockPrices,
        rockPriceHistory: { rock: [], stone: [], gem: [], meteorite: [], asteroid: [] },
        scoreMultiplier: 1,
        coinMultiplier: 1,
        customizationUnlocked: false,
        ownedCustomizations: ['default'],
        selectedCustomization: 'default',
        farmUnlocked: false,
        arcadeUnlocked: false,
      };
    }
  };

  const savedData = loadSavedData();

  const [gameState, setGameState] = useState<GameState>({
    position: 0,
    score: savedData.score,
    isMoving: false,
    lastKey: null,
  });
  const [isCelebrating, setIsCelebrating] = useState(false);
  const [coins, setCoins] = useState(savedData.coins);
  const [maxCoins, setMaxCoins] = useState(savedData.maxCoins);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [isStoreOpen, setIsStoreOpen] = useState(false);
  const [isAchievementsOpen, setIsAchievementsOpen] = useState(false);
  const [isStoreUnlocked, setIsStoreUnlocked] = useState(savedData.storeUnlocked);
  const [isAchievementsUnlocked, setIsAchievementsUnlocked] = useState(savedData.achievementsUnlocked);
  const [isAutomationUnlocked, setIsAutomationUnlocked] = useState(savedData.automationUnlocked);
  const [isGamblingUnlocked, setIsGamblingUnlocked] = useState(savedData.gamblingUnlocked);
  const [isGamblingOpen, setIsGamblingOpen] = useState(false);
  const [hasUsedGambling, setHasUsedGambling] = useState(savedData.hasUsedGambling);
  const [gamblingWins, setGamblingWins] = useState(savedData.gamblingWins);
  const [gamblingLosses, setGamblingLosses] = useState(savedData.gamblingLosses);
  const [gamblingCoinsWon, setGamblingCoinsWon] = useState(savedData.gamblingCoinsWon);
  const [gamblingCoinsSpent, setGamblingCoinsSpent] = useState(savedData.gamblingCoinsSpent);
  const [gamblingAutoplayUnlocked, setGamblingAutoplayUnlocked] = useState(savedData.gamblingAutoplayUnlocked);
  const [gamblingSlotCount, setGamblingSlotCount] = useState(savedData.gamblingSlotCount);
  const [isMicroUnlocked, setIsMicroUnlocked] = useState(savedData.microUnlocked);
  const [isMicroOpen, setIsMicroOpen] = useState(false);
  const [shinyRocks, setShinyRocks] = useState(savedData.shinyRocks);
  const [shinyUpgrades, setShinyUpgrades] = useState<{ parentValidation: number; overpricedCoffee: number }>(savedData.shinyUpgrades);
  const [mahjongPurchased, setMahjongPurchased] = useState(savedData.mahjongPurchased);
  const [upgrades, setUpgrades] = useState(savedData.upgrades);
  const [automation, setAutomation] = useState(savedData.automation);
  const [isRockMarketUnlocked, setIsRockMarketUnlocked] = useState(savedData.rockMarketUnlocked);
  const [rockHoldings, setRockHoldings] = useState<RockHoldings>(savedData.rockHoldings);
  const [rockPrices, setRockPrices] = useState<RockPrices>(savedData.rockPrices);
  const [rockPriceHistory, setRockPriceHistory] = useState<RockPriceHistory>(savedData.rockPriceHistory);
  const [isCustomizationUnlocked, setIsCustomizationUnlocked] = useState(savedData.customizationUnlocked);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [ownedCustomizations, setOwnedCustomizations] = useState<BoulderCustomization[]>(savedData.ownedCustomizations);
  const [selectedCustomization, setSelectedCustomization] = useState<BoulderCustomization>(
    (savedData.selectedCustomization as BoulderCustomization) || 'default'
  );
  const [isFarmUnlocked, setIsFarmUnlocked] = useState(savedData.farmUnlocked || false);
  const [isFarmOpen, setIsFarmOpen] = useState(false);
  const [scoreMultiplier, setScoreMultiplier] = useState(savedData.scoreMultiplier);
  const [coinMultiplier, setCoinMultiplier] = useState(savedData.coinMultiplier);
  const [isDropBallConfirmOpen, setIsDropBallConfirmOpen] = useState(false);
  const [showDropBallAnimation, setShowDropBallAnimation] = useState(false);
  const [showNgPlusUpgrade, setShowNgPlusUpgrade] = useState(false);
  const [zoomerMode, setZoomerMode] = useState(false);
  const prevScoreRef = useRef(savedData.score);
  const automationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const GOAL_POSITION = 750;
  const MOVE_DISTANCE = 50;
  const BASE_COINS_PER_ASCENSION = 1;

  // Score-based rank system
  const getRank = (score: number): { title: string; icon: string; color: string } => {
    if (score >= 1000) return { title: 'CTO', icon: '👑', color: '#9b59b6' };
    if (score >= 500) return { title: 'Tech Lead', icon: '🎯', color: '#e67e22' };
    if (score >= 200) return { title: 'Senior Developer', icon: '💼', color: '#3498db' };
    if (score >= 50) return { title: 'Mid-Level Developer', icon: '💻', color: '#2ecc71' };
    if (score >= 10) return { title: 'Junior Developer', icon: '👨‍💻', color: '#f39c12' };
    return { title: 'Intern', icon: '☕', color: '#95a5a6' };
  };

  const handleMove = useCallback((key: string) => {
    setGameState(prevState => {
      // Check if this is the correct alternating key
      if (prevState.lastKey === key) {
        return prevState; // Same key pressed twice, ignore
      }

      const newPosition = prevState.position + MOVE_DISTANCE;
      
          // Check if reached the goal
          if (newPosition >= GOAL_POSITION) {
            const baseScoreIncrease = 1 + upgrades.boulderSize + automation.cicd;
            const scoreIncrease = Math.floor(baseScoreIncrease * scoreMultiplier);
            return {
              position: 0,
              score: prevState.score + scoreIncrease,
              isMoving: true,
              lastKey: key,
            };
          }

      return {
        ...prevState,
        position: newPosition,
        isMoving: true,
        lastKey: key,
      };
    });

    // Reset movement animation after a short delay
    setTimeout(() => {
      setGameState(prevState => ({
        ...prevState,
        isMoving: false,
      }));
    }, 200);
  }, [upgrades.boulderSize, automation.cicd, scoreMultiplier]);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    const key = event.key.toLowerCase();
    
    if (key === 'a' || key === 'd') {
      handleMove(key);
    }
  }, [handleMove]);

  const handleButtonPress = useCallback((key: string) => {
    handleMove(key);
  }, [handleMove]);

  const handleStoreClick = useCallback(() => {
    setIsStoreOpen(true);
  }, []);

  const handleStoreClose = useCallback(() => {
    setIsStoreOpen(false);
  }, []);

  const handleUpgrade = useCallback((upgradeType: 'happiness' | 'productivity' | 'boulderSize') => {
    const getUpgradeCost = (level: number): number => {
      return Math.floor(10 * Math.pow(1.5, level));
    };

    const cost = getUpgradeCost(upgrades[upgradeType]);
    
    if (coins >= cost && upgrades[upgradeType] < 10) {
      setCoins(prevCoins => prevCoins - cost);
      setUpgrades((prevUpgrades: { happiness: number; productivity: number; boulderSize: number }) => ({
        ...prevUpgrades,
        [upgradeType]: prevUpgrades[upgradeType] + 1,
      }));
    }
  }, [coins, upgrades]);

  const handlePurchaseFeature = useCallback((feature: 'achievements' | 'automation' | 'mahjong' | 'gambling' | 'microtransactions' | 'rockMarket' | 'customization' | 'farm') => {
    const costs = {
      achievements: 50,
      automation: 100,
      mahjong: 100,
      gambling: 100,
      microtransactions: 1000,
      rockMarket: 1000,
      customization: 200,
      farm: 1000000,
    };

    const cost = costs[feature];
    
    if (coins >= cost) {
      setCoins(prevCoins => prevCoins - cost);
      if (feature === 'achievements') {
        setIsAchievementsUnlocked(true);
      } else if (feature === 'automation') {
        setIsAutomationUnlocked(true);
      } else if (feature === 'mahjong') {
        setMahjongPurchased(true);
      } else if (feature === 'gambling') {
        setIsGamblingUnlocked(true);
      } else if (feature === 'microtransactions') {
        setIsMicroUnlocked(true);
      } else if (feature === 'rockMarket') {
        setIsRockMarketUnlocked(true);
      } else if (feature === 'customization') {
        setIsCustomizationUnlocked(true);
      } else if (feature === 'farm') {
        setIsFarmUnlocked(true);
      }
    }
  }, [coins]);

  const handleAchievementsClick = useCallback(() => {
    setIsAchievementsOpen(true);
  }, []);

  const handleAchievementsClose = useCallback(() => {
    setIsAchievementsOpen(false);
  }, []);

  const handleGamblingOpen = useCallback(() => {
    setHasUsedGambling(true);
    setIsGamblingOpen(true);
  }, []);

  const handleGamblingClose = useCallback(() => {
    setIsGamblingOpen(false);
  }, []);

  const handleGamblingCoinsChange = useCallback((delta: number) => {
    setCoins(prev => Math.max(0, prev + delta));
    // Track spending (negative delta means coins spent)
    if (delta < 0) {
      setGamblingCoinsSpent(prev => {
        const newTotal = prev + Math.abs(delta);
        // Unlock autoplay when 100 coins have been spent
        if (newTotal >= 100 && !gamblingAutoplayUnlocked) {
          setGamblingAutoplayUnlocked(true);
        }
        return newTotal;
      });
    }
  }, [gamblingAutoplayUnlocked]);

  const handleGamblingResult = useCallback((result: 'win' | 'lose', payout: number) => {
    if (result === 'win') {
      setGamblingWins(prev => prev + 1);
      setGamblingCoinsWon(prev => prev + payout);
    } else {
      setGamblingLosses(prev => prev + 1);
    }
  }, []);

  const handleGamblingSlotUpgrade = useCallback(() => {
    setGamblingSlotCount(prev => {
      const newCount = Math.min(5, prev + 1);
      return newCount;
    });
  }, []);

  const handleMicroOpen = useCallback(() => {
    setIsMicroOpen(true);
  }, []);

  const handleMicroClose = useCallback(() => {
    setIsMicroOpen(false);
  }, []);

  const handleBuyShinyRocks = useCallback(() => {
    // Grant rocks for free; Microtransactions modal handles the UX message.
    setShinyRocks(prev => prev + 1000);
  }, []);

  const handleShinyUpgrade = useCallback((type: 'parentValidation' | 'overpricedCoffee') => {
    const cost = 100;
    if (shinyRocks >= cost && shinyUpgrades[type] < 10) {
      setShinyRocks(prev => prev - cost);
      setShinyUpgrades(prev => ({
        ...prev,
        [type]: prev[type] + 1,
      }));
    }
  }, [shinyRocks, shinyUpgrades]);

  const handleAutomationUpgrade = useCallback((type: 'manager' | 'devConsole' | 'cicd') => {
    const getUpgradeCost = (level: number): number => {
      return Math.floor(20 * Math.pow(1.8, level));
    };

    const cost = getUpgradeCost(automation[type]);
    
    if (coins >= cost && automation[type] < 10) {
      setCoins(prevCoins => prevCoins - cost);
      setAutomation((prevAutomation: { manager: number; devConsole: number; cicd: number }) => ({
        ...prevAutomation,
        [type]: prevAutomation[type] + 1,
      }));
    }
  }, [coins, automation]);

  const handleBuyRocks = useCallback((type: RockType, amount: number) => {
    const cost = amount * rockPrices[type];
    if (coins >= cost) {
      setCoins(prev => prev - cost);
      setRockHoldings(prev => ({
        ...prev,
        [type]: prev[type] + amount,
      }));
    }
  }, [coins, rockPrices]);

  const handleSellRocks = useCallback((type: RockType, amount: number) => {
    if (rockHoldings[type] >= amount) {
      setRockHoldings(prev => ({
        ...prev,
        [type]: prev[type] - amount,
      }));
      setCoins(prev => prev + amount * rockPrices[type]);
    }
  }, [rockHoldings, rockPrices]);

  const handleCustomizationOpen = useCallback(() => {
    setIsCustomizationOpen(true);
  }, []);

  const handleCustomizationClose = useCallback(() => {
    setIsCustomizationOpen(false);
  }, []);

  const handleCustomizationPurchase = useCallback((customization: BoulderCustomization) => {
    const cost = CUSTOMIZATIONS[customization].cost;
    if (coins >= cost && !ownedCustomizations.includes(customization)) {
      setCoins(prev => prev - cost);
      setOwnedCustomizations(prev => [...prev, customization]);
    }
  }, [coins, ownedCustomizations]);

  const handleCustomizationSelect = useCallback((customization: BoulderCustomization) => {
    if (ownedCustomizations.includes(customization)) {
      setSelectedCustomization(customization);
    }
  }, [ownedCustomizations]);

  const handleFarmOpen = useCallback(() => {
    setIsFarmOpen(true);
  }, []);

  const handleFarmClose = useCallback(() => {
    setIsFarmOpen(false);
  }, []);

  const handleFarmCoinsChange = useCallback((delta: number) => {
    setCoins(prev => Math.max(0, prev + delta));
  }, []);

  const handleDropBallClick = useCallback(() => {
    setIsDropBallConfirmOpen(true);
  }, []);

  const handleDropBallConfirm = useCallback(() => {
    setIsDropBallConfirmOpen(false);
    setShowDropBallAnimation(true);
  }, []);

  const handleDropBallAnimationComplete = useCallback(() => {
    setShowDropBallAnimation(false);
    setShowNgPlusUpgrade(true);
  }, []);

  const handleScoreMultiplierUpgrade = useCallback(() => {
    const cost = 100 * Math.pow(10, scoreMultiplier);
    if (gameState.score >= cost) {
      setGameState(prev => ({
        ...prev,
        score: prev.score - cost,
      }));
      setScoreMultiplier(prev => prev + 1);
    }
  }, [gameState.score, scoreMultiplier]);

  const handleCoinMultiplierUpgrade = useCallback(() => {
    const cost = 100 * Math.pow(10, coinMultiplier);
    if (coins >= cost) {
      setCoins(prev => prev - cost);
      setCoinMultiplier(prev => prev + 1);
    }
  }, [coins, coinMultiplier]);

  const handleRollBack = useCallback(() => {
    setGameState({
      position: 0,
      score: 0,
      isMoving: false,
      lastKey: null,
    });
    setCoins(0);
    setMaxCoins(0);
    setUpgrades({ happiness: 0, productivity: 0, boulderSize: 0 });
    setAutomation({ manager: 0, devConsole: 0, cicd: 0 });
    setRockHoldings({ rock: 0, stone: 0, gem: 0, meteorite: 0, asteroid: 0 });
    setRockPrices({
      rock: ROCK_TYPES.rock.basePrice,
      stone: ROCK_TYPES.stone.basePrice,
      gem: ROCK_TYPES.gem.basePrice,
      meteorite: ROCK_TYPES.meteorite.basePrice,
      asteroid: ROCK_TYPES.asteroid.basePrice,
    });
    setRockPriceHistory({ rock: [], stone: [], gem: [], meteorite: [], asteroid: [] });
    setIsStoreUnlocked(false);
    setIsAchievementsUnlocked(false);
    setIsAutomationUnlocked(false);
    setIsGamblingUnlocked(false);
    setIsMicroUnlocked(false);
    setIsRockMarketUnlocked(false);
    setIsCustomizationUnlocked(false);
    setMahjongPurchased(false);
    setShinyRocks(0);
    setShinyUpgrades({ parentValidation: 0, overpricedCoffee: 0 });
    setOwnedCustomizations(['default']);
    setSelectedCustomization('default');
    setGamblingWins(0);
    setGamblingLosses(0);
    setGamblingCoinsWon(0);
    setGamblingCoinsSpent(0);
    setGamblingAutoplayUnlocked(false);
    setHasUsedGambling(false);
    setShowNgPlusUpgrade(false);
    prevScoreRef.current = 0;
  }, []);

  // Automation auto-push logic
  useEffect(() => {
    if (isAutomationUnlocked && automation.manager > 0) {
      const interval = Math.max(1000, (11 - automation.manager) * 1000);
      
      const autoPush = () => {
        setGameState(prevState => {
          // Check if already at goal or moving
          if (prevState.position >= GOAL_POSITION || prevState.isMoving) {
            return prevState;
          }

          const lastKey = prevState.lastKey;
          const nextKey = lastKey === 'a' ? 'd' : 'a';
          const newPosition = prevState.position + MOVE_DISTANCE;
          
              // Check if reached the goal
              if (newPosition >= GOAL_POSITION) {
                const baseScoreIncrease = 1 + upgrades.boulderSize + automation.cicd;
                const scoreIncrease = Math.floor(baseScoreIncrease * scoreMultiplier);
                return {
                  position: 0,
                  score: prevState.score + scoreIncrease,
                  isMoving: true,
                  lastKey: nextKey,
                };
              }

          return {
            ...prevState,
            position: newPosition,
            isMoving: true,
            lastKey: nextKey,
          };
        });

        // Reset movement animation after a short delay
        setTimeout(() => {
          setGameState(prevState => ({
            ...prevState,
            isMoving: false,
          }));
        }, 200);
      };

      automationIntervalRef.current = setInterval(autoPush, interval);

      return () => {
        if (automationIntervalRef.current) {
          clearInterval(automationIntervalRef.current);
          automationIntervalRef.current = null;
        }
      };
    } else {
      if (automationIntervalRef.current) {
        clearInterval(automationIntervalRef.current);
        automationIntervalRef.current = null;
      }
    }
  }, [isAutomationUnlocked, automation.manager, upgrades.boulderSize, automation.cicd, scoreMultiplier]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // Save game data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('boulderGame_score', gameState.score.toString());
  }, [gameState.score]);

  useEffect(() => {
    localStorage.setItem('boulderGame_coins', coins.toString());
  }, [coins]);

  // Track highest amount of coins ever held for money achievements
  useEffect(() => {
    setMaxCoins(prev => {
      const next = coins > prev ? coins : prev;
      if (next !== prev) {
        localStorage.setItem('boulderGame_maxCoins', next.toString());
      }
      return next;
    });
  }, [coins]);

  useEffect(() => {
    localStorage.setItem('boulderGame_upgrades', JSON.stringify(upgrades));
  }, [upgrades]);

  useEffect(() => {
    localStorage.setItem('boulderGame_storeUnlocked', isStoreUnlocked.toString());
  }, [isStoreUnlocked]);

  useEffect(() => {
    localStorage.setItem('boulderGame_achievementsUnlocked', isAchievementsUnlocked.toString());
  }, [isAchievementsUnlocked]);

  useEffect(() => {
    localStorage.setItem('boulderGame_automationUnlocked', isAutomationUnlocked.toString());
  }, [isAutomationUnlocked]);

  useEffect(() => {
    localStorage.setItem('boulderGame_gamblingUnlocked', isGamblingUnlocked.toString());
  }, [isGamblingUnlocked]);

  useEffect(() => {
    localStorage.setItem('boulderGame_microUnlocked', isMicroUnlocked.toString());
  }, [isMicroUnlocked]);

  useEffect(() => {
    localStorage.setItem('boulderGame_hasUsedGambling', hasUsedGambling.toString());
  }, [hasUsedGambling]);

  useEffect(() => {
    localStorage.setItem('boulderGame_gamblingWins', gamblingWins.toString());
  }, [gamblingWins]);

  useEffect(() => {
    localStorage.setItem('boulderGame_gamblingLosses', gamblingLosses.toString());
  }, [gamblingLosses]);

  useEffect(() => {
    localStorage.setItem('boulderGame_gamblingCoinsWon', gamblingCoinsWon.toString());
  }, [gamblingCoinsWon]);

  useEffect(() => {
    localStorage.setItem('boulderGame_gamblingCoinsSpent', gamblingCoinsSpent.toString());
  }, [gamblingCoinsSpent]);

  useEffect(() => {
    localStorage.setItem('boulderGame_gamblingAutoplayUnlocked', gamblingAutoplayUnlocked.toString());
  }, [gamblingAutoplayUnlocked]);

  useEffect(() => {
    localStorage.setItem('boulderGame_shinyRocks', shinyRocks.toString());
  }, [shinyRocks]);

  useEffect(() => {
    localStorage.setItem('boulderGame_shinyUpgrades', JSON.stringify(shinyUpgrades));
  }, [shinyUpgrades]);

  useEffect(() => {
    localStorage.setItem('boulderGame_mahjongPurchased', mahjongPurchased.toString());
  }, [mahjongPurchased]);

  useEffect(() => {
    localStorage.setItem('boulderGame_rockMarketUnlocked', isRockMarketUnlocked.toString());
  }, [isRockMarketUnlocked]);

  useEffect(() => {
    localStorage.setItem('boulderGame_customizationUnlocked', isCustomizationUnlocked.toString());
  }, [isCustomizationUnlocked]);

  useEffect(() => {
    localStorage.setItem('boulderGame_ownedCustomizations', JSON.stringify(ownedCustomizations));
  }, [ownedCustomizations]);

  useEffect(() => {
    localStorage.setItem('boulderGame_selectedCustomization', selectedCustomization);
  }, [selectedCustomization]);

  useEffect(() => {
    localStorage.setItem('boulderGame_farmUnlocked', isFarmUnlocked.toString());
  }, [isFarmUnlocked]);


  useEffect(() => {
    localStorage.setItem('boulderGame_rockHoldings', JSON.stringify(rockHoldings));
  }, [rockHoldings]);

  useEffect(() => {
    localStorage.setItem('boulderGame_rockPrices', JSON.stringify(rockPrices));
  }, [rockPrices]);

  useEffect(() => {
    localStorage.setItem('boulderGame_rockPriceHistory', JSON.stringify(rockPriceHistory));
  }, [rockPriceHistory]);

  useEffect(() => {
    localStorage.setItem('boulderGame_automation', JSON.stringify(automation));
  }, [automation]);

  // Unlock store when reaching 10 coins
  useEffect(() => {
    if (coins >= 10 && !isStoreUnlocked) {
      setIsStoreUnlocked(true);
    }
  }, [coins, isStoreUnlocked]);

  // Trigger celebration and award coins when score increases
  useEffect(() => {
    if (gameState.score > prevScoreRef.current) {
      setIsCelebrating(true);
      // Award coins with productivity bonus and dev console bonus
      const baseCoins = BASE_COINS_PER_ASCENSION + upgrades.productivity;
      const devConsoleBonus = automation.devConsole;
      const coinsToAward = Math.floor((baseCoins + devConsoleBonus) * coinMultiplier);
      
      setCoins(prevCoins => prevCoins + coinsToAward);
      setCoinsEarned(coinsToAward);
      prevScoreRef.current = gameState.score;
      
      // Update rock prices on each ascension
      setRockPrices(prevPrices => {
        setRockPriceHistory(prev => ({
          rock: [...(prev.rock || []), prevPrices.rock].slice(-10),
          stone: [...(prev.stone || []), prevPrices.stone].slice(-10),
          gem: [...(prev.gem || []), prevPrices.gem].slice(-10),
          meteorite: [...(prev.meteorite || []), prevPrices.meteorite].slice(-10),
          asteroid: [...(prev.asteroid || []), prevPrices.asteroid].slice(-10),
        }));
        return {
          rock: calculateNewPrice(prevPrices.rock, 'rock'),
          stone: calculateNewPrice(prevPrices.stone, 'stone'),
          gem: calculateNewPrice(prevPrices.gem, 'gem'),
          meteorite: calculateNewPrice(prevPrices.meteorite, 'meteorite'),
          asteroid: calculateNewPrice(prevPrices.asteroid, 'asteroid'),
        };
      });
      
      // Reset celebration after animation
      const timer = setTimeout(() => {
        setIsCelebrating(false);
        setCoinsEarned(0);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [gameState.score, upgrades.productivity, automation.devConsole, coinMultiplier]);

  return (
    <div className={`boulder-game ${showNgPlusUpgrade ? 'ng-plus-mode' : ''}`}>
      {/* Zoomer mode toggle */}
      <div className="zoomer-toggle-container">
        <label className="zoomer-toggle">
          <input
            type="checkbox"
            checked={zoomerMode}
            onChange={(e) => setZoomerMode(e.target.checked)}
          />
          <span className="zoomer-toggle-label">Zoomer Mode</span>
        </label>
      </div>

      {/* Store and unlockable buttons */}
      <div className="top-buttons">
        {isStoreUnlocked && (
          <button 
            className="store-button"
            onClick={handleStoreClick}
            onTouchStart={(e) => {
              e.preventDefault();
              handleStoreClick();
            }}
            aria-label="Open Store"
          >
            <span className="store-icon">🏪</span>
          </button>
        )}
        
        {isAchievementsUnlocked && (
          <button 
            className="top-button achievements-button"
            onClick={handleAchievementsClick}
            onTouchStart={(e) => {
              e.preventDefault();
              handleAchievementsClick();
            }}
            aria-label="Achievements"
          >
            <span className="button-icon">🏆</span>
          </button>
        )}
        
        {isAutomationUnlocked && (
          <button 
            className="top-button automation-button"
            onClick={() => {
              const automationElement = document.querySelector('.automation-container');
              if (automationElement) {
                automationElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              const automationElement = document.querySelector('.automation-container');
              if (automationElement) {
                automationElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            aria-label="Automation"
          >
            <span className="button-icon">⚙️</span>
          </button>
        )}
        
        {isGamblingUnlocked && (
          <button
            className="top-button gambling-button"
            onClick={handleGamblingOpen}
            onTouchStart={(e) => {
              e.preventDefault();
              handleGamblingOpen();
            }}
            aria-label="Gambling Addiction"
          >
            <span className="button-icon">🎰</span>
          </button>
        )}

        {isMicroUnlocked && (
          <button
            className="top-button micro-button"
            onClick={handleMicroOpen}
            onTouchStart={(e) => {
              e.preventDefault();
              handleMicroOpen();
            }}
            aria-label="Microtransactions"
          >
            <span className="button-icon">💎</span>
          </button>
        )}

        {isCustomizationUnlocked && (
          <button
            className="top-button customization-button"
            onClick={handleCustomizationOpen}
            onTouchStart={(e) => {
              e.preventDefault();
              handleCustomizationOpen();
            }}
            aria-label="Customization"
          >
            <span className="button-icon">🎨</span>
          </button>
        )}

        {isFarmUnlocked && (
          <button
            className="top-button farm-button"
            onClick={handleFarmOpen}
            onTouchStart={(e) => {
              e.preventDefault();
              handleFarmOpen();
            }}
            aria-label="Farm"
          >
            <span className="button-icon">🌾</span>
          </button>
        )}
      </div>
      
      <div className="game-header">
        <h1>
          {(() => {
            const rank = getRank(gameState.score);
            return `${rank.title} Life Simulator`;
          })()}
        </h1>
        {(scoreMultiplier > 1 || coinMultiplier > 1) && (
          <div className="ng-plus-badge">
            Score: {scoreMultiplier}x • Coins: {coinMultiplier}x
          </div>
        )}
        <div className="stats-container">
          <div className={`score ${isCelebrating ? 'celebrating' : ''}`}>
            <span className="score-label">Score: </span>
            <span className="score-value">{gameState.score}</span>
            {isCelebrating && (
              <>
                <div className="celebration-sparkles">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className={`sparkle sparkle-${i}`}>✨</div>
                  ))}
                </div>
                <div className="celebration-particles">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className={`particle particle-${i}`}>🎉</div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className={`coins ${coinsEarned > 0 ? 'coins-earned' : ''}`}>
            <span className="coin-icon">🪙</span>
            <span className="coin-value">{coins}</span>
            {coinsEarned > 0 && (
              <div className="coin-earned-indicator">
                +{coinsEarned}
              </div>
            )}
          </div>
        </div>
        <div className="instructions">
          <span className="desktop-instruction">Press 'A' and 'D' alternately to push the boulder up the mountain! This is definitely not a metaphor for anything.</span>
          <span className="mobile-instruction">Tap the buttons below alternately to push the boulder up the mountain! This is definitely not a metaphor for anything.</span>
        </div>
      </div>
      
      <div className="game-container">
        <Mountain />
        <Stickman 
          position={gameState.position} 
          isMoving={gameState.isMoving}
          hasPartyHat={upgrades.happiness >= 10}
        />
        <Boulder 
          position={gameState.position + 40} 
          isMoving={gameState.isMoving}
          size={upgrades.boulderSize}
          customization={selectedCustomization}
        />
        
        {/* Goal line */}
        <div 
          className="goal-line"
          style={{ left: `${(GOAL_POSITION / 900) * 100}%` }}
        />
      </div>

      {/* Mobile control buttons */}
      <div className="mobile-controls">
        <button 
          className="control-button control-button-a"
          onTouchStart={(e) => {
            e.preventDefault();
            handleButtonPress('a');
          }}
          onClick={() => handleButtonPress('a')}
          aria-label="Press A"
        >
          A
        </button>
        <button 
          className="control-button control-button-d"
          onTouchStart={(e) => {
            e.preventDefault();
            handleButtonPress('d');
          }}
          onClick={() => handleButtonPress('d')}
          aria-label="Press D"
        >
          D
        </button>
      </div>

      {/* Drop the ball button */}
      {gameState.score >= 100 && !showNgPlusUpgrade && (
        <button
          className="drop-ball-button"
          onClick={handleDropBallClick}
        >
          Drop the ball
        </button>
      )}

      {/* Automation and Rock Market sections - shown below game when unlocked */}
      {!showNgPlusUpgrade && (
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {isAutomationUnlocked && (
            <div style={{ flex: '1', minWidth: '300px' }}>
              <Automation
                automation={automation}
                coins={coins}
                onUpgrade={handleAutomationUpgrade}
              />
            </div>
          )}
          {isRockMarketUnlocked && (
            <div style={{ flex: '1', minWidth: '300px' }}>
              <RockMarket
                holdings={rockHoldings}
                prices={rockPrices}
                priceHistory={rockPriceHistory}
                coins={coins}
                onBuy={handleBuyRocks}
                onSell={handleSellRocks}
              />
            </div>
          )}
        </div>
      )}

      {/* Store popup */}
      <Store
        isOpen={isStoreOpen}
        onClose={handleStoreClose}
        coins={coins}
        upgrades={upgrades}
        onUpgrade={handleUpgrade}
        isAchievementsUnlocked={isAchievementsUnlocked}
        isAutomationUnlocked={isAutomationUnlocked}
        mahjongPurchased={mahjongPurchased}
        isGamblingUnlocked={isGamblingUnlocked}
        isMicroUnlocked={isMicroUnlocked}
        isRockMarketUnlocked={isRockMarketUnlocked}
        isCustomizationUnlocked={isCustomizationUnlocked}
        isFarmUnlocked={isFarmUnlocked}
        onPurchaseFeature={handlePurchaseFeature}
      />

      {/* Achievements popup */}
      <Achievements
        isOpen={isAchievementsOpen}
        onClose={handleAchievementsClose}
        score={gameState.score}
        coins={maxCoins}
        hasUsedGambling={hasUsedGambling}
        gamblingWins={gamblingWins}
        gamblingLosses={gamblingLosses}
        gamblingCoinsWon={gamblingCoinsWon}
      />

      {/* Gambling Addiction modal */}
      <Gambling
        isOpen={isGamblingOpen}
        onClose={handleGamblingClose}
        coins={coins}
        onCoinsChange={handleGamblingCoinsChange}
        onSpinResult={handleGamblingResult}
        autoplayUnlocked={gamblingAutoplayUnlocked}
        coinsSpent={gamblingCoinsSpent}
        slotCount={gamblingSlotCount}
        onUpgradeSlots={handleGamblingSlotUpgrade}
      />

      {/* Microtransactions modal */}
      <Microtransactions
        isOpen={isMicroOpen}
        onClose={handleMicroClose}
        shinyRocks={shinyRocks}
        onBuyShinyRocks={handleBuyShinyRocks}
        shinyUpgrades={shinyUpgrades}
        onUpgrade={handleShinyUpgrade}
      />

      {/* Customization modal */}
      <Customization
        isOpen={isCustomizationOpen}
        onClose={handleCustomizationClose}
        coins={coins}
        ownedCustomizations={ownedCustomizations}
        selectedCustomization={selectedCustomization}
        onPurchase={handleCustomizationPurchase}
        onSelect={handleCustomizationSelect}
      />


      {/* Zoomer mode gifs */}
      {zoomerMode && (
        <>
          <div className="zoomer-gif-container zoomer-gif-left">
            <img 
              src={`${process.env.PUBLIC_URL}/subway.gif`}
              alt="Zoomer mode"
              className="zoomer-gif"
            />
          </div>
          <div className="zoomer-gif-container zoomer-gif-right">
            <img 
              src={`${process.env.PUBLIC_URL}/fortnite.gif`}
              alt="Fortnite zoomer mode"
              className="zoomer-gif"
            />
          </div>
        </>
      )}

      {/* Drop Ball Confirmation Modal */}
      {isDropBallConfirmOpen && (
        <div className="drop-ball-confirm-overlay" onClick={() => setIsDropBallConfirmOpen(false)}>
          <div className="drop-ball-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <button className="drop-ball-confirm-close" onClick={() => setIsDropBallConfirmOpen(false)}>×</button>
            <h2>Drop the ball?</h2>
            <p>Are you really sure you want to drop the ball? The boulder will roll down and... well, you'll see what happens.</p>
            <div className="drop-ball-confirm-actions">
              <button className="drop-ball-confirm-yes" onClick={handleDropBallConfirm}>
                Yes, drop it!
              </button>
              <button className="drop-ball-confirm-no" onClick={() => setIsDropBallConfirmOpen(false)}>
                No, keep pushing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drop Ball Animation */}
      {showDropBallAnimation && (
        <DropBallAnimation onComplete={handleDropBallAnimationComplete} />
      )}

      {/* NG+ Upgrade Screen */}
      {showNgPlusUpgrade && (
        <NgPlusUpgrade
          score={gameState.score}
          coins={coins}
          scoreMultiplier={scoreMultiplier}
          coinMultiplier={coinMultiplier}
          onUpgradeScore={handleScoreMultiplierUpgrade}
          onUpgradeCoin={handleCoinMultiplierUpgrade}
          onRollBack={handleRollBack}
        />
      )}

      {/* Farm modal */}
      <Farm
        isOpen={isFarmOpen}
        onClose={handleFarmClose}
        coins={coins}
        onCoinsChange={handleFarmCoinsChange}
      />
    </div>
  );
};

export default BoulderGame; 